import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Plus, Search, X, Printer, Eye, Pencil, Trash2,
  ShoppingCart, User, CheckCircle, DollarSign, CreditCard
} from 'lucide-react';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import ConfirmModal from '../../components/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import JalaliDatePicker, { JalaliDateRangeFilter } from '../../components/JalaliDatePicker';
import { saleService, customerService, medicineService, settingsService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali, toJalaliDateTime } from '../../utils/jalali';
import toast from 'react-hot-toast';

// ─── Medicine Lookup ──────────────────────────────────────────────────────────
function MedicineLookup({ onAdd }) {
  const [q, setQ] = useState(''); const [res, setR] = useState([]); const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const ref = useRef(null); const tmr = useRef(null);
  useEffect(() => { const h=(e)=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);}; document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h); }, []);
  const doSearch = useCallback(async(val)=>{ if(!val){setR([]);setOpen(false);return;} setBusy(true); try{const r=await medicineService.searchWithBatches(val);setR(r.data.data||[]);setOpen(true);}catch{}finally{setBusy(false);} }, []);
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input value={q} onChange={e=>{setQ(e.target.value);clearTimeout(tmr.current);tmr.current=setTimeout(()=>doSearch(e.target.value),280);}}
          onFocus={()=>q&&setOpen(true)} placeholder="Search medicine by name or barcode..."
          className="w-full pl-9 pr-8 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"/>
        {busy&&<div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"/>}
        {q&&!busy&&<button onClick={()=>{setQ('');setR([]);setOpen(false);}} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"><X size={14}/></button>}
      </div>
      {open&&res.length>0&&(
        <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {res.map(m=>(
            <button key={m._id} type="button" onClick={()=>{onAdd(m);setQ('');setR([]);setOpen(false);}}
              className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-50 last:border-0">
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">
                    {m.commercialName}
                    {m._isBatch && m.batchNumber && (
                      <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
                        Batch: {m.batchNumber}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">
                    {m.genericName}
                    {m.strength  ? ` · ${m.strength}`  : ''}
                    {m.drugForm  ? ` · ${m.drugForm}`   : ''}
                    {m.expiryDate ? ` · Exp: ${new Date(m.expiryDate).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-sm font-bold text-blue-700">{formatCurrency(m.sellingPrice)}</p>
                  <p className="text-xs text-gray-400">Avail: {m.availableQty||0} {m.baseUnit}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open&&res.length===0&&q&&!busy&&<div className="absolute top-full left-0 right-0 z-30 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-4 text-center text-sm text-gray-400">No medicines found</div>}
    </div>
  );
}

// ─── Customer Lookup ──────────────────────────────────────────────────────────
function CustomerLookup({ value, onChange, onSelect }) {
  const [results, setR] = useState([]); const [open, setOpen] = useState(false); const [mode, setMode] = useState('manual');
  const ref = useRef(null); const tmr = useRef(null);
  useEffect(()=>{const h=(e)=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);},[]);
  const doSearch = async(q)=>{ if(!q||q.length<1){setR([]);return;} try{const r=await customerService.getAll({search:q,limit:8});setR(r.data.data||[]);setOpen(true);}catch{} };
  return (
    <div className="relative" ref={ref}>
      <div className="flex gap-2 mb-2">
        <button type="button" onClick={()=>setMode('manual')} className={`text-xs px-2.5 py-1 rounded-lg font-medium ${mode==='manual'?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>Walk-in / Manual</button>
        <button type="button" onClick={()=>setMode('search')} className={`text-xs px-2.5 py-1 rounded-lg font-medium ${mode==='search'?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>From List</button>
      </div>
      <div className="relative">
        <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input value={value} onChange={e=>{onChange(e.target.value);if(mode==='search'){clearTimeout(tmr.current);tmr.current=setTimeout(()=>doSearch(e.target.value),300);}}}
          placeholder={mode==='manual'?'Customer name (optional)':'Search customer...'}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
      </div>
      {open&&results.length>0&&mode==='search'&&(
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {results.map(c=>(
            <button key={c._id} type="button" onClick={()=>{onSelect(c);setOpen(false);}}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 text-sm">
              <p className="font-medium text-gray-800">{c.name}</p>
              <div className="flex justify-between">
                <p className="text-xs text-gray-400">{c.phone}</p>
                {c.totalDebt>0&&<p className="text-xs text-red-500 font-medium">Debt: {formatCurrency(c.totalDebt)}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Professional Invoice (مطابق نمونه) ──────────────────────────────────────
function InvoicePrint({ sale, settings = {} }) {
  if (!sale) return null;
  const pharmacyName = settings.company_name    || 'PharmaCare Pharmacy';
  const phone1       = settings.company_phone   || '';
  const phone2       = settings.company_phone2  || '';
  const address      = settings.company_address || '';
  const logo         = settings.company_logo    || null;
  const footer       = settings.invoice_footer  || 'Thank you for your business!';
  const showLogo     = settings.show_logo_on_invoice !== 'false';
  const showSig      = settings.show_signature_line  !== 'false';
  const currency     = settings.default_currency || 'AFN';
  const displayCustomer = sale.customerName || sale.customer?.name || 'Walk-in';

  const rows     = sale.items || [];
  const subtotal = sale.subtotal       || 0;
  const discount = sale.discountAmount || 0;
  const tax      = sale.taxAmount      || 0;
  const total    = sale.totalAmount    || 0;
  const paid     = sale.paidAmount     || 0;

  const BLUE = '#1e40af';

  return (
    <div id="invoice-print" style={{ fontFamily:"'Inter',-apple-system,sans-serif", fontSize:'12px', width:'100%', maxWidth:'720px', margin:'0 auto', padding:'16px', color:'#000', direction:'ltr' }}>

      {/* ── HEADER: left=invoice info | center=pharmacy | right=logo ── */}
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:'12px', paddingBottom:'10px', borderBottom:`2px solid ${BLUE}` }}>
        <tbody><tr>
          <td style={{ width:'160px', verticalAlign:'middle', fontSize:'11px', color:'#444' }}>
            <div><strong>Invoice #:</strong> {sale.invoiceNumber}</div>
            <div><strong>Date:</strong> {toJalali(sale.createdAt)}</div>
            <div><strong>Payment:</strong> <span style={{ textTransform:'capitalize' }}>{sale.paymentMethod}</span></div>
          </td>
          <td style={{ textAlign:'center', verticalAlign:'middle' }}>
            <div style={{ fontWeight:'700', fontSize:'17px', marginBottom:'3px' }}>{pharmacyName}</div>
            {(phone1||phone2) && <div style={{ fontSize:'11px', color:'#555' }}>{[phone1,phone2].filter(Boolean).join(' | ')}</div>}
            {address && <div style={{ fontSize:'11px', color:'#555' }}>{address}</div>}
          </td>
          <td style={{ width:'160px', textAlign:'right', verticalAlign:'middle' }}>
            {showLogo && logo
              ? <img src={logo} alt="Logo" style={{ maxHeight:'65px', maxWidth:'150px', objectFit:'contain' }}/>
              : showLogo && <div style={{ width:'70px', height:'50px', border:'1px dashed #ccc', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'9px', color:'#bbb' }}>LOGO</div>
            }
          </td>
        </tr></tbody>
      </table>

      {/* ── Customer row ── */}
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:'10px', fontSize:'11px' }}>
        <tbody><tr>
          <td><strong>Customer:</strong> {displayCustomer}{sale.customer?.phone ? ` | ${sale.customer.phone}` : ''}</td>
          <td style={{ textAlign:'right' }}><strong>Currency:</strong> {currency}</td>
        </tr></tbody>
      </table>

      {/* ── Items table — BLUE header only ── */}
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
        <thead>
          <tr style={{ backgroundColor: BLUE, color:'#fff' }}>
            <th style={{ border:`1px solid ${BLUE}`, padding:'6px 8px', width:'32px', textAlign:'center' }}>#</th>
            <th style={{ border:`1px solid ${BLUE}`, padding:'6px 8px', textAlign:'left' }}>Medicine Name</th>
            <th style={{ border:`1px solid ${BLUE}`, padding:'6px 8px', width:'55px', textAlign:'center' }}>Qty</th>
            <th style={{ border:`1px solid ${BLUE}`, padding:'6px 8px', width:'65px', textAlign:'center' }}>Unit</th>
            <th style={{ border:`1px solid ${BLUE}`, padding:'6px 8px', width:'90px', textAlign:'right' }}>Unit Price</th>
            <th style={{ border:`1px solid ${BLUE}`, padding:'6px 8px', width:'90px', textAlign:'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, i) => (
            <tr key={i} style={{ backgroundColor: i%2===1 ? '#f8faff' : '#fff' }}>
              <td style={{ border:'1px solid #d1d5db', padding:'5px 8px', textAlign:'center' }}>{i+1}</td>
              <td style={{ border:'1px solid #d1d5db', padding:'5px 8px', textAlign:'left' }}>{item.medicineName || item.medicine?.commercialName}</td>
              <td style={{ border:'1px solid #d1d5db', padding:'5px 8px', textAlign:'center' }}>{item.quantity}</td>
              <td style={{ border:'1px solid #d1d5db', padding:'5px 8px', textAlign:'center', color:'#666' }}>{item.unit}</td>
              <td style={{ border:'1px solid #d1d5db', padding:'5px 8px', textAlign:'right' }}>{(item.unitPrice||0).toLocaleString()}</td>
              <td style={{ border:'1px solid #d1d5db', padding:'5px 8px', textAlign:'right', fontWeight:'600' }}>{(item.totalPrice||0).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Totals — bottom-left, matches image layout ── */}
      <table style={{ borderCollapse:'collapse', marginTop:'12px', fontSize:'12px', minWidth:'260px' }}>
        <tbody>
          <tr>
            <td style={{ padding:'4px 12px', textAlign:'right', fontWeight:'600', borderBottom:'1px dashed #ccc', minWidth:'140px' }}>Subtotal:</td>
            <td style={{ padding:'4px 12px', borderBottom:'1px dashed #ccc', minWidth:'90px' }}>{subtotal.toLocaleString()}</td>
          </tr>
          {discount > 0 && <tr>
            <td style={{ padding:'4px 12px', textAlign:'right', color:'#c00' }}>Discount:</td>
            <td style={{ padding:'4px 12px', color:'#c00' }}>- {discount.toLocaleString()}</td>
          </tr>}
          {tax > 0 && <tr>
            <td style={{ padding:'4px 12px', textAlign:'right' }}>Tax:</td>
            <td style={{ padding:'4px 12px' }}>{tax.toLocaleString()}</td>
          </tr>}
          <tr>
            <td style={{ padding:'5px 12px', textAlign:'right', fontWeight:'700', borderTop:`2px solid ${BLUE}` }}>Grand Total:</td>
            <td style={{ padding:'5px 12px', fontWeight:'700', fontSize:'13px', borderTop:`2px solid ${BLUE}` }}>{total.toLocaleString()}</td>
          </tr>
          <tr>
            <td style={{ padding:'4px 12px', textAlign:'right', color:'#555', borderBottom:'1px dashed #ccc' }}>Paid:</td>
            <td style={{ padding:'4px 12px', borderBottom:'1px dashed #ccc' }}>{paid.toLocaleString()}</td>
          </tr>
          {total - paid > 0 && <tr>
            <td style={{ padding:'4px 12px', textAlign:'right', color:'#c00', fontWeight:'600' }}>Balance Due:</td>
            <td style={{ padding:'4px 12px', color:'#c00', fontWeight:'600' }}>{(total-paid).toLocaleString()}</td>
          </tr>}
        </tbody>
      </table>

      {sale.notes && <div style={{ fontSize:'10px', color:'#666', marginTop:'8px' }}>Note: {sale.notes}</div>}

      {/* ── Dashed line + footer ── */}
      <div style={{ borderTop:'1px dashed #aaa', marginTop:'20px', paddingTop:'8px', textAlign:'center', fontSize:'10px', color:'#777' }}>
        {footer}
      </div>

      {/* ── Signatures ── */}
      {showSig && (
        <table style={{ width:'100%', borderCollapse:'collapse', marginTop:'28px' }}>
          <tbody><tr>
            <td style={{ textAlign:'left', fontSize:'11px', width:'50%' }}>
              Seller Signature
              <div style={{ marginTop:'28px', borderTop:'1px solid #888', width:'130px' }}/>
            </td>
            <td style={{ textAlign:'right', fontSize:'11px', width:'50%' }}>
              Customer Signature
              <div style={{ marginTop:'28px', borderTop:'1px solid #888', width:'130px', marginLeft:'auto' }}/>
            </td>
          </tr></tbody>
        </table>
      )}
    </div>
  );
}

// ─── Main SalesPage ───────────────────────────────────────────────────────────
export default function SalesPage() {
  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [fStatus, setFS]        = useState('');
  const [fPay, setFP]           = useState('');
  const [dateFrom, setDF]       = useState('');
  const [dateTo, setDT]         = useState('');
  const [settings, setSettings] = useState({});

  // POS state
  const [posOpen, setPosOpen]         = useState(false);
  const [editSale, setEditSale]       = useState(null);
  const [viewSale, setViewSale]       = useState(null);
  const [successSale, setSuccessSale] = useState(null);
  const [deleteTarget, setDT2]        = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const [saving, setSaving]           = useState(false);

  // Cart
  const [cartItems, setCart]   = useState([]);
  const [custName, setCustName]= useState('');
  const [custId, setCustId]    = useState('');
  const [custDebt, setCustDebt]= useState(0);
  const [payMethod, setPay]    = useState('cash');
  const [discount, setDisc]    = useState(0);
  const [taxAmt, setTaxAmt]    = useState(0);
  const [paidAmt, setPaidAmt]  = useState('');
  const [notes, setNotes]      = useState('');

  // Payment on invoice
  const [payOnInvoice, setPayOnInvoice]   = useState(false);
  const [invoicePayAmt, setInvPayAmt]     = useState('');
  const [invoicePayMethod, setInvPayMth]  = useState('cash');
  const [payingInvoice, setPayingInvoice] = useState(false);

  const subtotal    = cartItems.reduce((s,i)=>s+i.totalPrice, 0);
  // COGS: quantity × conversionToBase × purchasePrice
  const totalCOGS   = cartItems.reduce((s,i)=>{
    const conv = i.unit === i.baseUnit ? 1 : ((i.salesUnits||[]).find(u=>u.unitKey===i.unit)?.conversionToBase || 1);
    return s + Number(i.purchasePrice||0) * Number(i.quantity) * conv;
  }, 0);
  const grossProfit = subtotal - totalCOGS;
  const netRevenue  = Math.max(0, subtotal - Number(discount) + Number(taxAmt));
  const netProfit   = netRevenue - totalCOGS;
  const profitPct   = netRevenue > 0 ? ((netProfit/netRevenue)*100).toFixed(1) : 0;
  const totalAmount = netRevenue;
  const changeAmt   = Math.max(0, Number(paidAmt) - totalAmount);

  useEffect(() => {
    fetchSales();
    settingsService.getAll().then(r=>setSettings(r.data.settings||{})).catch(()=>{});
  }, [page, search, fStatus, fPay, dateFrom, dateTo]); // eslint-disable-line

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = { page, limit:20 };
      if (search)  params.search = search;
      if (fStatus) params.status = fStatus;
      if (fPay)    params.paymentStatus = fPay;
      if (dateFrom) params.from = dateFrom;
      if (dateTo)   params.to   = dateTo;
      const res = await saleService.getAll(params);
      setData(res.data.data||[]);
      setTotal(res.data.pagination?.total||0);
    } catch { toast.error('Failed to load sales'); }
    finally { setLoading(false); }
  };

  const openPOS = () => {
    setCart([]); setCustName(''); setCustId(''); setCustDebt(0);
    setPay('cash'); setDisc(0); setTaxAmt(0); setPaidAmt(''); setNotes('');
    setEditSale(null); setPosOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await saleService.getById(row._id);
      const s   = res.data.data;
      setEditSale(s);
      setCustName(s.customerName||s.customer?.name||'');
      setCustId(s.customer?._id||'');
      setCustDebt(s.customerDebt||0);
      setPay(s.paymentMethod||'cash');
      setDisc(s.discountAmount||0);
      setTaxAmt(s.taxAmount||0);
      setPaidAmt(s.paidAmount||0);
      setNotes(s.notes||'');
      setCart(s.items.map(item=>({
        medicineId:   item.medicine?._id||item.medicine,
        medicineName: item.medicineName||item.medicine?.commercialName,
        salesUnits:   item.medicine?.salesUnits||[],
        baseUnit:     item.medicine?.baseUnit||'piece',
        quantity:     item.quantity,
        unit:         item.unit,
        unitPrice:    item.unitPrice,
        discount:     item.discount||0,
        totalPrice:   item.totalPrice,
        batchNumber:  item.batchNumber||'',
      })));
      setPosOpen(true);
    } catch { toast.error('Failed to load sale'); }
  };

  const openView = async (row) => {
    try {
      const res = await saleService.getById(row._id);
      setViewSale(res.data.data);
      setPayOnInvoice(false); setInvPayAmt(''); setInvPayMth('cash');
    } catch { toast.error('Failed to load'); }
  };

  const addToCart = (med) => {
    const ex = cartItems.find(i=>i.medicineId===med._id);
    if (ex) {
      setCart(cartItems.map(i=>i.medicineId===med._id?{...i,quantity:i.quantity+1,totalPrice:(i.quantity+1)*i.unitPrice-i.discount}:i));
    } else {
      const defUnit = med.defaultSaleUnit||med.baseUnit||'piece';
      setCart([...cartItems,{
        medicineId:     med._id,
        medicineName:   med.commercialName,
        salesUnits:     med.salesUnits||[],
        baseUnit:       med.baseUnit||'strip',
        quantity:       1,
        unit:           defUnit,
        unitPrice:      med.sellingPrice||0,
        _baseSellPrice: med.sellingPrice||0,   // ← always the base unit price
        purchasePrice:  med.purchasePrice||0,
        batchNumber: med.batchNumber || '',
        discount:       0,
        totalPrice:     med.sellingPrice||0,
        batchNumber:    ''
      }]);
    }
  };

  const updateCart = (idx, field, val) => setCart(prev => prev.map((item, i) => {
    if (i !== idx) return item;
    // NEVER overwrite _baseSellPrice — it's the anchor price for base unit
    const u = { ...item, [field]: val, _baseSellPrice: item._baseSellPrice };

    if (field === 'unit') {
      const base = item._baseSellPrice || 0;
      if (val === item.baseUnit) {
        // back to base unit → restore base price
        u.unitPrice = base;
      } else {
        // find the sales unit
        const su = (item.salesUnits||[]).find(s => s.unitKey === val);
        if (su) {
          // use defined sellingPrice OR calculate from base
          u.unitPrice = (su.sellingPrice > 0) ? su.sellingPrice : base * su.conversionToBase;
        }
      }
    }

    if (['quantity','unitPrice','discount','unit'].includes(field)) {
      u.totalPrice = Math.max(0, Number(u.quantity) * Number(u.unitPrice) - Number(u.discount||0));
    }
    return u;
  }));

  const handleSubmit = async () => {
    if (!cartItems.length) return toast.error('Cart is empty');
    setSaving(true);
    try {
      const isCredit = payMethod === 'credit';
      // paidAmt خالی = کاربر چیزی وارد نکرده
      // اگر نسیه → صفر
      // اگر مبلغ وارد شده → همان
      // اگر خالی + نقد → کل مبلغ (نقد کامل)
      const paidAmtNum = paidAmt === '' || paidAmt === null || paidAmt === undefined
        ? null
        : Number(paidAmt);
      const paid = isCredit
        ? 0
        : paidAmtNum !== null
          ? paidAmtNum
          : totalAmount; // blank = full cash payment
      const payload = {
        customerName: custId ? undefined : (custName||undefined),
        customer:     custId||undefined,
        items: cartItems.map(i=>({
          medicine:      i.medicineId,
          medicineName:  i.medicineName,
          quantity:      Number(i.quantity),
          unit:          i.unit,
          unitPrice:     Number(i.unitPrice),
          purchasePrice: Number(i.purchasePrice||0),  // برای COGS
          discount:      Number(i.discount)||0,
          totalPrice:    Number(i.totalPrice),
          batchNumber:   i.batchNumber||'',
        })),
        subtotal, discountAmount:Number(discount), taxAmount:Number(taxAmt),
        totalAmount, paidAmount:paid, changeAmount:changeAmt,
        paymentMethod:payMethod,
        paymentStatus: paid===0?'unpaid':paid>=totalAmount?'paid':'partial',
        notes,
      };
      let res;
      if (editSale) { res=await saleService.update(editSale._id,payload); toast.success('Sale updated'); }
      else { res=await saleService.create(payload); toast.success('Sale completed!'); }
      setSuccessSale(res.data.data);
      setPosOpen(false);
      fetchSales();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await saleService.delete(deleteTarget._id); toast.success('Sale cancelled, inventory restored'); fetchSales(); }
    catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setDeleting(false); setDT2(null); }
  };

  const handlePayOnInvoice = async () => {
    if (!invoicePayAmt||Number(invoicePayAmt)<=0) return toast.error('Enter a valid amount');
    setPayingInvoice(true);
    try {
      await saleService.recordPayment(viewSale._id, { amount: Number(invoicePayAmt), method: invoicePayMethod });
      toast.success('Payment recorded');
      setPayOnInvoice(false); setInvPayAmt('');
      // Refresh view
      const res = await saleService.getById(viewSale._id);
      setViewSale(res.data.data);
      fetchSales();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setPayingInvoice(false); }
  };

  const printInvoice = (sale) => {
    const content = document.getElementById('invoice-print-area');
    if (!content) return;
    const win = window.open('','_blank','width=800,height=700');
    win.document.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">
      <title>Invoice - ${sale?.invoiceNumber||''}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter',-apple-system,sans-serif; background: white; padding: 0; direction: ltr; }
        @media print { @page { size: A4; margin: 8mm; } body { padding: 0; } }
      </style>
      </head><body>${content.innerHTML}</body></html>`);
    win.document.close();
    setTimeout(()=>win.print(), 500);
  };

  const statusColor = {completed:'green',cancelled:'red',returned:'orange'};
  const payColor    = {paid:'green',partial:'blue',unpaid:'red'};
  const payLabel    = {paid:'Paid',partial:'Partial Payment',unpaid:'Unpaid / Credit'};

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Sales" subtitle="Sales management and POS"
        actions={<Button onClick={openPOS}><ShoppingCart size={16}/> New Sale</Button>}/>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-40">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search invoice or customer..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <select value={fStatus} onChange={e=>setFS(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none">
          <option value="">All Status</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
        </select>
        <select value={fPay} onChange={e=>setFP(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none">
          <option value="">All Payments</option><option value="paid">Paid</option><option value="partial">Partial</option><option value="unpaid">Unpaid</option>
        </select>
        <JalaliDatePicker value={dateFrom} onChange={v=>setDF(v)} placeholder="From date" className="w-40"/>
        <JalaliDatePicker value={dateTo} onChange={v=>setDT(v)} placeholder="To date" className="w-40"/>
        {(search||fStatus||fPay||dateFrom||dateTo)&&<button onClick={()=>{setSearch('');setFS('');setFP('');setDF('');setDT('');}} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"><X size={12}/>Clear</button>}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Invoice</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Customer</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Items</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Total</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Paid</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-red-400">Balance Due</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading?Array(5).fill(0).map((_,i)=><tr key={i}>{Array(9).fill(0).map((_,j)=><td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td>)}</tr>):
              data.length===0?<tr><td colSpan={10} className="text-center py-12 text-gray-400">No sales found</td></tr>:
              data.map(row=>(
                <tr key={row._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-blue-700 font-medium">{row.invoiceNumber}</td>
                  <td className="px-4 py-3 text-gray-700">{row.customerName||row.customer?.name||<span className="text-gray-300">Walk-in</span>}</td>
                  <td className="px-4 py-3 text-center text-gray-500">{row.items?.length}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-800">{formatCurrency(row.totalAmount)}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{formatCurrency(row.paidAmount)}</td>
                  <td className="px-4 py-3 text-right">{row.totalAmount-row.paidAmount>0?<span className="text-red-500 font-medium text-xs">{formatCurrency(row.totalAmount-row.paidAmount)}</span>:<span className="text-green-500 text-xs">—</span>}</td>
                  <td className="px-4 py-3"><Badge color={payColor[row.paymentStatus]||'gray'}>{payLabel[row.paymentStatus]||row.paymentStatus}</Badge></td>
                  <td className="px-4 py-3"><Badge color={statusColor[row.status]||'gray'}>{row.status}</Badge></td>
                  <td className="px-4 py-3 text-xs text-gray-400">{toJalali(row.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={()=>openView(row)} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="View"><Eye size={14}/></button>
                      {row.status!=='cancelled'&&<button onClick={()=>openEdit(row)} className="p-1.5 rounded hover:bg-yellow-50 text-gray-400 hover:text-yellow-600" title="Edit"><Pencil size={14}/></button>}
                      <button onClick={()=>{ setSuccessSale(row); setTimeout(()=>printInvoice(row),300); }} className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600" title="Print"><Printer size={14}/></button>
                      {row.status!=='cancelled'&&<button onClick={()=>setDT2(row)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Cancel"><Trash2 size={14}/></button>}
                    </div>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
        {total>20&&(
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,total)} of {total}</p>
            <div className="flex gap-1">
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
              {Array.from({length:Math.min(5,Math.ceil(total/20))},(_,i)=>{const p=page<=3?i+1:page-2+i;if(p<1||p>Math.ceil(total/20))return null;return<button key={p} onClick={()=>setPage(p)} className={`w-8 h-8 text-xs rounded border ${p===page?'bg-blue-600 text-white border-blue-600':'border-gray-200 hover:bg-gray-50'}`}>{p}</button>;})}
              <button onClick={()=>setPage(p=>p+1)} disabled={page*20>=total} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {data.map(row=>(
          <div key={row._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div><p className="text-xs font-mono text-blue-700">{row.invoiceNumber}</p><p className="text-sm font-medium text-gray-800">{row.customerName||row.customer?.name||'Walk-in'}</p></div>
              <div className="flex gap-1"><Badge color={statusColor[row.status]||'gray'}>{row.status}</Badge><Badge color={payColor[row.paymentStatus]||'gray'}>{row.paymentStatus}</Badge></div>
            </div>
            <div className="flex justify-between text-sm mb-3"><span className="text-gray-400">{toJalali(row.createdAt)} · {row.items?.length} item(s)</span><span className="font-bold">{formatCurrency(row.totalAmount)}</span></div>
            <div className="flex gap-2 pt-2 border-t border-gray-50">
              <button onClick={()=>openView(row)} className="flex-1 text-xs py-1.5 rounded bg-blue-50 text-blue-700 font-medium">View</button>
              {row.status!=='cancelled'&&<button onClick={()=>openEdit(row)} className="flex-1 text-xs py-1.5 rounded bg-yellow-50 text-yellow-700 font-medium">Edit</button>}
              <button onClick={()=>{setSuccessSale(row);setTimeout(()=>printInvoice(row),300);}} className="flex-1 text-xs py-1.5 rounded bg-green-50 text-green-700 font-medium">Print</button>
              {row.status!=='cancelled'&&<button onClick={()=>setDT2(row)} className="flex-1 text-xs py-1.5 rounded bg-red-50 text-red-700 font-medium">Cancel</button>}
            </div>
          </div>
        ))}
      </div>

      {/* POS Modal */}
      <Modal isOpen={posOpen} onClose={()=>setPosOpen(false)} title={editSale?'Edit Sale':'New Sale'} size="xl">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left: Search + Cart */}
          <div className="flex-1 space-y-3">
            <MedicineLookup onAdd={addToCart}/>
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-100"><p className="text-xs font-semibold text-gray-600 uppercase">Cart ({cartItems.length})</p></div>
              {cartItems.length===0?<div className="py-10 text-center text-gray-300 text-sm">Add medicines above</div>:(
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Medicine</th>
                        <th className="text-center px-2 py-2 text-xs font-medium text-gray-400 w-20">Qty</th>
                        <th className="text-left px-2 py-2 text-xs font-medium text-gray-400 w-24">Unit</th>
                        <th className="text-right px-2 py-2 text-xs font-medium text-gray-400 w-24">Price</th>
                        <th className="text-right px-2 py-2 text-xs font-medium text-gray-400 w-20">Disc.</th>
                        <th className="text-right px-2 py-2 text-xs font-medium text-gray-400 w-24">Total</th>
                        <th className="w-8"/>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {cartItems.map((item,idx)=>(
                        <tr key={idx}>
                          <td className="px-3 py-2"><p className="text-xs font-medium text-gray-800">{item.medicineName}</p></td>
                          <td className="px-2 py-2">
                            <div className="flex items-center justify-center gap-1">
                              <button type="button" onClick={()=>updateCart(idx,'quantity',Math.max(1,item.quantity-1))} className="w-5 h-5 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 text-xs flex items-center justify-center">-</button>
                              <span className="w-6 text-center text-xs font-medium">{item.quantity}</span>
                              <button type="button" onClick={()=>updateCart(idx,'quantity',item.quantity+1)} className="w-5 h-5 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 text-xs flex items-center justify-center">+</button>
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <select value={item.unit} onChange={e=>updateCart(idx,'unit',e.target.value)} className="text-xs border border-gray-200 rounded px-1 py-1 w-full bg-white focus:outline-none">
                              {[
                {unitKey:item.baseUnit,unitName:item.baseUnit+' (base)',conversionToBase:1},
                ...(item.salesUnits||[])
              ].map(u=>(
                <option key={u.unitKey} value={u.unitKey}>
                  {u.unitName}{u.conversionToBase>1?` (=${u.conversionToBase} ${item.baseUnit})`:''}
                </option>
              ))}
                            </select>
                          </td>
                          <td className="px-2 py-2"><input type="number" value={item.unitPrice} min="0" step="0.01" onChange={e=>updateCart(idx,'unitPrice',e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1 w-full text-right focus:outline-none"/></td>
                          <td className="px-2 py-2"><input type="number" value={item.discount} min="0" onChange={e=>updateCart(idx,'discount',e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1 w-full text-right focus:outline-none"/></td>
                          <td className="px-2 py-2 text-right text-xs font-semibold text-gray-800">{formatCurrency(item.totalPrice)}</td>
                          <td className="px-1 py-2"><button type="button" onClick={()=>setCart(p=>p.filter((_,i)=>i!==idx))} className="text-red-300 hover:text-red-600 p-1"><X size={13}/></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Right: Summary */}
          <div className="w-full lg:w-72 space-y-3 flex-shrink-0">
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Customer</p>
              <CustomerLookup value={custName} onChange={setCustName} onSelect={(c)=>{setCustId(c._id);setCustName(c.name);setCustDebt(c.totalDebt||0);}}/>
              {custDebt>0&&<div className="mt-2 flex items-center gap-1.5 text-xs bg-red-50 text-red-700 px-3 py-2 rounded-lg border border-red-100"><CreditCard size={12}/> Existing debt: <strong>{formatCurrency(custDebt)}</strong></div>}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Payment</p>
              <FormField label="Payment Method">
                <select value={payMethod} onChange={e=>{ setPay(e.target.value); if(e.target.value==='credit') setPaidAmt(''); }} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="cash">Cash (نقد)</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="mobile">Mobile</option>
                  <option value="credit">Credit / Unpaid (pay later)</option>
                </select>
              </FormField>
              {payMethod === 'credit' && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                  <span className="text-orange-600 text-lg">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-orange-800">نسیه / Credit Sale</p>
                    <p className="text-xs text-orange-600">فاکتور ثبت می‌شود — بدهی مشتری اضافه می‌شود</p>
                  </div>
                </div>
              )}
              <FormField label="Discount (AFN)">
                <input type="number" value={discount} min="0" onChange={e=>setDisc(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              </FormField>
              <FormField label="Tax (AFN)">
                <input type="number" value={taxAmt} min="0" onChange={e=>setTaxAmt(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              </FormField>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 text-sm border border-gray-200">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
              <div className="flex justify-between text-gray-400 text-xs border-b border-gray-200 pb-1.5">
                <span>Cost (COGS)</span><span>{formatCurrency(totalCOGS)}</span>
              </div>
              <div className={`flex justify-between text-xs font-medium ${grossProfit>=0?'text-green-600':'text-red-500'}`}>
                <span>Net Profit ({profitPct}%)</span><span>{formatCurrency(netProfit)}</span>
              </div>
              {Number(discount)>0&&<div className="flex justify-between text-red-500"><span>Discount</span><span>-{formatCurrency(Number(discount))}</span></div>}
              {Number(taxAmt)>0&&<div className="flex justify-between text-gray-500"><span>Tax</span><span>+{formatCurrency(Number(taxAmt))}</span></div>}
              <div className="flex justify-between font-bold text-base text-gray-900 border-t border-gray-200 pt-2 mt-1"><span>Total</span><span className="text-blue-700">{formatCurrency(totalAmount)}</span></div>
              {payMethod !== 'credit' && (
                <FormField label="Amount Received">
                  <input type="number" value={paidAmt} min="0" onChange={e=>setPaidAmt(e.target.value)} placeholder={formatCurrency(totalAmount)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                </FormField>
              )}
              {changeAmt>0&&<div className="flex justify-between font-semibold text-green-700"><span>Change</span><span>{formatCurrency(changeAmt)}</span></div>}
            </div>

            <FormField label="Notes">
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notes"/>
            </FormField>

            <Button onClick={handleSubmit} loading={saving} className="w-full" size="lg" disabled={cartItems.length===0}>
              <ShoppingCart size={18}/> {editSale?'Update Sale':'Complete Sale'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Invoice Modal */}
      <Modal isOpen={!!viewSale} onClose={()=>setViewSale(null)} title="Invoice Details" size="xl">
        {viewSale && (
          <div>
            {/* Customer debt banner */}
            {(viewSale.customerDebt||0)>0&&(
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2">
                  <CreditCard size={18} className="text-red-600"/>
                  <div>
                    <p className="text-sm font-semibold text-red-800">Customer has outstanding debt</p>
                    <p className="text-xs text-red-500">{viewSale.customer?.name||viewSale.customerName}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-red-700">{formatCurrency(viewSale.customerDebt)}</p>
                  <button onClick={()=>setPayOnInvoice(true)} className="text-xs text-red-600 hover:text-red-800 underline">Record Payment</button>
                </div>
              </div>
            )}

            {/* Invoice unpaid portion */}
            {viewSale.totalAmount - viewSale.paidAmount > 0 && (
              <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4">
                <div>
                  <p className="text-sm font-medium text-orange-800">Invoice Balance Due</p>
                  <p className="text-xs text-orange-500">Invoice: {viewSale.invoiceNumber}</p>
                </div>
                <div className="text-right">
                  <p className="text-base font-bold text-orange-700">{formatCurrency(viewSale.totalAmount - viewSale.paidAmount)}</p>
                  <button onClick={()=>{setPayOnInvoice(true);setInvPayAmt(String(viewSale.totalAmount-viewSale.paidAmount));}} className="text-xs text-orange-600 hover:text-orange-800 underline">Pay Now</button>
                </div>
              </div>
            )}

            {/* Payment form */}
            {payOnInvoice && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 space-y-3">
                <p className="text-sm font-semibold text-blue-800 flex items-center gap-2"><DollarSign size={15}/> Record Payment</p>
                <div className="grid grid-cols-3 gap-3">
                  <FormField label="Amount">
                    <Input type="number" step="0.01" value={invoicePayAmt} onChange={e=>setInvPayAmt(e.target.value)} placeholder="0.00"/>
                  </FormField>
                  <FormField label="Method">
                    <Select value={invoicePayMethod} onChange={e=>setInvPayMth(e.target.value)}>
                      <option value="cash">Cash</option>
                      <option value="bank">Bank</option>
                      <option value="card">Card</option>
                      <option value="mobile">Mobile</option>
                    </Select>
                  </FormField>
                  <FormField label="&nbsp;">
                    <div className="flex gap-2 pt-1">
                      <Button onClick={handlePayOnInvoice} loading={payingInvoice} size="sm" className="bg-green-600 hover:bg-green-700 text-white">Pay</Button>
                      <Button variant="secondary" size="sm" onClick={()=>setPayOnInvoice(false)}>Cancel</Button>
                    </div>
                  </FormField>
                </div>
              </div>
            )}

            {/* ── Invoice Detail View ── */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm">
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Invoice Info</p>
                  <div className="flex justify-between"><span className="text-gray-500">Invoice #</span><span className="font-mono font-semibold text-blue-700">{viewSale.invoiceNumber}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Date</span><span>{toJalali(viewSale.createdAt)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Payment</span><span className="capitalize">{viewSale.paymentMethod}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Status</span><Badge color={viewSale.status==='completed'?'green':'red'}>{viewSale.status}</Badge></div>
                  <div className="flex justify-between"><span className="text-gray-500">Pay Status</span><Badge color={viewSale.paymentStatus==='paid'?'green':viewSale.paymentStatus==='partial'?'blue':'red'}>{viewSale.paymentStatus}</Badge></div>
                  <div className="flex justify-between"><span className="text-gray-500">Created By</span><span>{viewSale.createdBy?.name||'-'}</span></div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm">
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Customer</p>
                  <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium">{viewSale.customerName||viewSale.customer?.name||'Walk-in'}</span></div>
                  {viewSale.customer?.phone&&<div className="flex justify-between"><span className="text-gray-500">Phone</span><span>{viewSale.customer.phone}</span></div>}
                  {viewSale.customer?.address&&<div className="flex justify-between"><span className="text-gray-500">Address</span><span className="text-right text-xs max-w-[140px]">{viewSale.customer.address}</span></div>}
                  {(viewSale.customerDebt||0)>0&&<div className="flex justify-between pt-1 border-t border-gray-200"><span className="text-red-500 font-medium">Total Debt</span><span className="text-red-600 font-bold">{formatCurrency(viewSale.customerDebt)}</span></div>}
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100 flex justify-between items-center">
                  <p className="text-xs font-semibold text-gray-600 uppercase">Items ({viewSale.items?.length})</p>
                  <p className="text-xs text-gray-400">Branch: {viewSale.branch?.name||'-'}</p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 w-8">#</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Medicine</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-14">Qty</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-16">Unit</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 w-24">Unit Price</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20">Discount</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {viewSale.items?.map((item,i)=>(
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-gray-400 text-xs">{i+1}</td>
                        <td className="px-4 py-2.5"><p className="font-medium text-gray-800">{item.medicineName||item.medicine?.commercialName}</p><p className="text-xs text-gray-400">{item.medicine?.genericName}</p></td>
                        <td className="px-3 py-2.5 text-center font-medium">{item.quantity}</td>
                        <td className="px-3 py-2.5 text-center text-gray-500 text-xs capitalize">{item.unit}</td>
                        <td className="px-4 py-2.5 text-right">{formatCurrency(item.unitPrice)}</td>
                        <td className="px-3 py-2.5 text-right text-red-400 text-xs">{item.discount>0?`- ${formatCurrency(item.discount)}`:'-'}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-800">{formatCurrency(item.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <div className="w-60 space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatCurrency(viewSale.subtotal)}</span></div>
                  {(viewSale.discountAmount||0)>0&&<div className="flex justify-between text-red-500"><span>Discount</span><span>- {formatCurrency(viewSale.discountAmount)}</span></div>}
                  {(viewSale.taxAmount||0)>0&&<div className="flex justify-between text-gray-500"><span>Tax</span><span>+ {formatCurrency(viewSale.taxAmount)}</span></div>}
                  <div className="flex justify-between text-orange-500 text-sm"><span>Total COGS</span><span>{formatCurrency(viewSale.totalCOGS||0)}</span></div>
                  <div className="flex justify-between text-green-600 text-sm font-medium"><span>Gross Profit</span><span>{formatCurrency(viewSale.grossProfit||0)} ({viewSale.subtotal>0?((viewSale.grossProfit||0)/viewSale.subtotal*100).toFixed(1):0}%)</span></div>
                  <div className="flex justify-between font-bold text-base border-t border-gray-200 pt-1.5"><span>Grand Total</span><span className="text-blue-700">{formatCurrency(viewSale.totalAmount)}</span></div>
                  <div className="flex justify-between text-green-600"><span>Paid</span><span>{formatCurrency(viewSale.paidAmount)}</span></div>
                  {viewSale.totalAmount-viewSale.paidAmount>0&&<div className="flex justify-between font-semibold text-red-600 border-t border-gray-200 pt-1.5"><span>Balance Due</span><span>{formatCurrency(viewSale.totalAmount-viewSale.paidAmount)}</span></div>}
                  {viewSale.changeAmount>0&&<div className="flex justify-between text-gray-400"><span>Change</span><span>{formatCurrency(viewSale.changeAmount)}</span></div>}
                </div>
              </div>
              {viewSale.notes&&<div className="text-xs text-gray-500 bg-yellow-50 rounded-lg px-3 py-2 border border-yellow-100">Note: {viewSale.notes}</div>}
            </div>

            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
              <Button variant="secondary" onClick={()=>setViewSale(null)}>Close</Button>
              <Button onClick={()=>{setSuccessSale(viewSale);setTimeout(()=>printInvoice(viewSale),300);}}><Printer size={15}/> Print Invoice</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={!!successSale&&!posOpen} onClose={()=>setSuccessSale(null)} title="Sale Completed!" size="xl">
        {successSale && (
          <div>
            <div className="flex items-center gap-3 mb-4 p-3 bg-green-50 rounded-lg">
              <CheckCircle size={24} className="text-green-600"/>
              <div>
                <p className="font-semibold text-green-800">Sale recorded successfully</p>
                <p className="text-xs text-green-600">Invoice: {successSale.invoiceNumber} · Total: {formatCurrency(successSale.totalAmount)}</p>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <InvoicePrint sale={successSale} settings={settings}/>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
              <Button variant="secondary" onClick={()=>setSuccessSale(null)}>Close</Button>
              <Button onClick={()=>printInvoice(successSale)}><Printer size={15}/> Print Invoice</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Hidden print area */}
      <div className="hidden" aria-hidden="true">
        <div id="invoice-print-area">
          <InvoicePrint sale={successSale||viewSale} settings={settings}/>
        </div>
      </div>

      <ConfirmModal isOpen={!!deleteTarget} onClose={()=>setDT2(null)} onConfirm={handleDelete} loading={deleting}
        title="Cancel Sale" message={`Cancel invoice "${deleteTarget?.invoiceNumber}"? Inventory will be restored.`} confirmLabel="Cancel Sale"/>
    </div>
  );
}
